<template>
  <transition name="grow">
    <base-modal @close="$emit('close')" head="register_lora">
      <div class="flex flex-row space-x-8 items-center justify-between">
        <!-- Form -->
        <div v-if="!taskProgressStep" class="w-full" style="min-height: 280px">
          <div class="flex">
            <div class="w-3/5">
              <span class="mb-1 flex font-semibold">{{
                $t(`mygwf.components.Label.device_eui`)
              }}</span>
              <textarea
                v-model="textEuis"
                @paste="onPaste"
                class="h-72 w-full focus:outline-none focus:border-primary-light focus:bg-white overflow-y-scroll space-y-1 border p-4"
              >
              </textarea>
            </div>
            <div class="w-2/5 mx-4">
              <label class="font-semibold">{{ $t(`mygwf.components.Label.action`) }}</label>
              <div class="flex flex-col justify-center my-6 space-y-5">
                <label>
                  <input :value="true" v-model="isRegistering" type="radio" />
                  <span class="ml-2">{{ $t(`mygwf.components.LoraRegistration.register`) }}</span>
                </label>
                <label>
                  <input :value="false" v-model="isRegistering" type="radio" />
                  <span class="ml-2">{{ $t(`mygwf.components.LoraRegistration.unregister`) }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Task progress -->
        <div v-else class="w-full" style="min-height: 280px">
          <p class="flex items-center font-semibold">
            <span class="w-4 mr-2">
              <base-icon v-if="taskProgressStep === 1" class="icon-spin" icon="spinner-btn" />
              <span v-else class="text-green-dark">✔</span>
            </span>
            Starting {{ isRegistering ? 'register' : 'unregister' }} task
          </p>
          <p
            class="flex items-center font-semibold mt-1"
            :class="{'opacity-25': taskProgressStep < 2}"
          >
            <span class="w-4 mr-2">
              <base-icon v-if="taskProgressStep === 2" class="icon-spin" icon="spinner-btn" />
              <span v-if="taskProgressStep > 2" class="text-green-dark">✔</span>
            </span>
            Checking task progress
          </p>
          <p
            class="flex items-center font-semibold mt-1"
            :class="{'opacity-25': taskProgressStep < 3}"
          >
            <span class="w-4 mr-2">
              <span v-if="taskProgressStep === 3" class="text-green-dark">✔</span>
            </span>
            Task completed
          </p>
          <template v-if="taskProgressStep === 3">
            <hr class="my-4" />
            <p class="font-semibold mb-3">Results</p>
            <ul
              v-if="taskResultDetails.length > 0"
              class="overflow-y-auto border-2 p-2"
              style="max-height: 145px; min-height: 145px"
            >
              <li v-for="entry in taskResultDetails" :key="entry.eui">
                <span class="font-bold">{{ entry.eui }} - </span>
                <span :class="{'text-orange-default': entry.failed}">{{
                  capitalizeFirstLetter(entry.description)
                }}</span>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
        <div class="flex flex-col">
          <div class="flex space-x-4">
            <base-button class="w-1/2" @click="$emit('close')" action="close" context="secondary">
              <base-icon icon="close" />
            </base-button>
            <div class="w-1/2">
              <base-button
                v-if="isRegistering"
                @click="startTask('register')"
                key="register"
                :disabled="devices.length === 0 || !!taskProgressStep"
                action="register"
                :loading="loading"
                class="w-full"
              >
                <base-icon icon="register" />
              </base-button>
              <base-button
                v-else
                @click="startTask('unregister')"
                key="unregister"
                context="error"
                :disabled="devices.length === 0 || !!taskProgressStep"
                action="unregister"
                :loading="loading"
                class="w-full"
              >
                <base-icon icon="register" />
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </base-modal>
  </transition>
</template>

<script>
import {BaseAlert, BaseModal, BaseButton, BaseIcon} from '@/components/base'
import upperFirst from 'lodash-es/upperFirst'

export default {
  components: {
    BaseIcon,
    BaseAlert,
    BaseModal,
    BaseButton,
  },

  props: {
    items: {
      type: Array,
    },
  },

  data() {
    return {
      textEuis: '',
      loading: false,
      taskProgressStep: 0,
      taskInterval: null,
      taskResultDetails: [],
      isRegistering: true,
      capitalizeFirstLetter: upperFirst,
    }
  },

  computed: {
    companies() {
      return this.$store.getters['companies/list']
    },
    devices() {
      return this.textEuis.split('\n').filter(el => el !== '')
    },
  },

  mounted() {
    this.textEuis = this.items.reduce((acc, key) => [...acc, key?.data?.device_eui], []).join('\n')
  },

  beforeDestroy() {
    if (this.taskInterval) {
      this.uncheckTask()
    }
  },

  methods: {
    checkTask(id) {
      setTimeout(() => (this.taskProgressStep = 2), 1000)
      this.$store
        .dispatch(`devices/getTaskSwisscom`, id)
        .then(data => this.handleTaskResponse(data))
      this.taskInterval = setInterval(() => {
        this.$store
          .dispatch(`devices/getTaskSwisscom`, id)
          .then(data => this.handleTaskResponse(data))
      }, 5000)
    },

    handleTaskResponse(response) {
      switch (response.state || null) {
        case 'SUCCESS':
          const devicesState = response.detail.reduce((acc, cur) => {
            const eui = Object.keys(cur)[0]
            acc[eui] = cur[eui]
            return acc
          }, {})

          this.taskResultDetails = Object.keys(devicesState).map(device => {
            const entry = {
              eui: device,
              description: devicesState[device],
              failed: false,
            }
            if (entry.description !== 'registered') {
              entry.description = entry.description.replace('Bad request:', '').trim()
              entry.failed = true
            }
            return entry
          })
          this.$emit('success')
          this.loading = false
          this.uncheckTask()
          setTimeout(() => (this.taskProgressStep = 3), 1000)
          break
        case 'FAILURE':
          this.taskResultDetails = [
            {
              eui: null,
              description: response.detail.description,
            },
          ]
          this.loading = false
          this.uncheckTask()
          setTimeout(() => (this.taskProgressStep = 3), 1000)
          break
        default:
          break
      }
    },

    handleTaskResponseError(response) {
      const devicesState = response.detail.devices.reduce((acc, cur) => {
        const eui = Object.keys(cur)[0]
        acc[eui] = cur[eui]
        return acc
      }, {})

      this.taskResultDetails = Object.keys(devicesState).map(device => {
        return {
          eui: device,
          description: devicesState[device],
          failed: true,
        }
      })
      this.loading = false
      this.uncheckTask()
      setTimeout(() => (this.taskProgressStep = 3), 1000)
    },

    uncheckTask() {
      return clearInterval(this.taskInterval)
    },

    onPaste() {
      setTimeout(() => {
        this.textEuis = this.textEuis.replace(/[,]+/g, '\n').replace(/['" ]+/g, '')
      }, 10)
    },

    async startTask(type) {
      try {
        this.loading = true
        this.taskProgressStep = 1
        const task = type === 'register' ? 'devices/postSwisscom' : 'devices/deleteSwisscom'
        const taskData = await this.$store.dispatch(task, this.devices)
        this.checkTask(taskData.split(':')[1].trim())
      } catch (e) {
        this.handleTaskResponseError(e)
        this.loading = false
      }
    },
  },
}
</script>
